import { forwardRef, type CSSProperties, type ImgHTMLAttributes } from "react";

type OptimizedPictureProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src" | "srcSet"
> & {
  src: string;
  imgClassName?: string;
  pictureClassName?: string;
  style?: CSSProperties;
};

function derivedSources(src: string) {
  const match = src.match(/^(.*)\.(png|jpe?g)$/i);
  if (!match) {
    return { avif: null as string | null, webp: null as string | null, fallback: src };
  }
  const base = match[1];
  return {
    avif: `${base}.avif`,
    webp: `${base}.webp`,
    fallback: src,
  };
}

export default forwardRef<HTMLImageElement, OptimizedPictureProps>(
  function OptimizedPicture(
    {
      src,
      alt = "",
      className,
      imgClassName,
      pictureClassName,
      ...rest
    },
    ref
  ) {
    const { avif, webp, fallback } = derivedSources(src);
    const imgClass = imgClassName ?? className;
    const imgEl = (
      <img ref={ref} src={fallback} alt={alt} className={imgClass} {...rest} />
    );

    if (!avif || !webp) {
      return imgEl;
    }

    return (
      <picture className={pictureClassName ?? className}>
        <source srcSet={avif} type="image/avif" />
        <source srcSet={webp} type="image/webp" />
        {imgEl}
      </picture>
    );
  }
);

export function optimizedImageSources(src: string) {
  return derivedSources(src);
}
